import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/Auth0Context.jsx';
import apiService from '../services/apiService';
import { API_CONFIG } from '../config/api.js';
import { Modal } from './base-ui/Modal';
import { Button } from './base-ui/Button';
import { Input } from './base-ui/Input';
import { Select } from './base-ui/Select';
import { Textarea } from './base-ui/Textarea';
import CampaignSlotEditor from './CampaignSlotEditor.jsx';
import { queueStageCutoutForCharacters } from '../services/stageCutoutQueue.js';
import useMobileBreakpoint from '../hooks/useMobileBreakpoint';
import {
  buildCharacterImagePayloadForWrite,
  hasPortraitImageSource,
} from '../utils/characterImageAssets.js';
import './CampaignSetup.css';

const getIncompleteSlotsValidation = (slots, { requireNamedCharacter = false } = {}) => {
  const namedSlots = slots.filter(slot => slot.name && slot.name.trim() !== '');
  const incompleteSlots = [];

  namedSlots.forEach(slot => {
    const missingFields = [];
    if (!slot.character_class || slot.character_class.trim() === '') missingFields.push('Class');
    if (!slot.race || slot.race.trim() === '') missingFields.push('Race');
    if (!slot.gender || slot.gender.trim() === '') missingFields.push('Gender');
    if (!slot.age_category || slot.age_category.trim() === '') missingFields.push('Age');
    // Voice is intentionally NOT required here. Pregenerated characters have no
    // voice_id and the quick-pick grid has no voice UI, so requiring it blocked
    // the common "tap a portrait to start" path (BOU-1348). The backend assigns
    // a gender-aware voice for any character missing one during creation
    // (character_manager.create_character_from_simple → voice_pool.assign_voice,
    // with a reused-character backfill), so a missing voice_id never blocks start.
    if (!hasPortraitImageSource(slot)) missingFields.push('Portrait');

    if (missingFields.length > 0) {
      incompleteSlots.push({
        name: slot.name,
        missingFields,
        characterId: slot.existing_character_id || slot.character_id || slot.external_character_id || null,
      });
    }
  });

  const missingRequiredCharacter = requireNamedCharacter && namedSlots.length === 0;

  return {
    isValid: incompleteSlots.length === 0 && !missingRequiredCharacter,
    incompleteSlots,
    missingRequiredCharacter,
    hasAnyNamedCharacters: namedSlots.length > 0,
    allSlotsEmpty: namedSlots.length === 0
  };
};

const DEFAULT_CAMPAIGN_STYLE = 'epic_adventure';

const DEFAULT_PRESET_DIFFICULTY = {
  casual_fun: 'easy',
  epic_adventure: 'medium',
  dark_souls: 'deadly',
  political_intrigue: 'medium',
  dungeon_crawl: 'hard',
  action_movie: 'easy'
};

const DEFAULT_PRESET_LIST = [
  {
    value: 'casual_fun',
    label: 'Casual Fun',
    description: 'Lighthearted adventures with friendly NPCs and low stakes',
    settings: {
      tone: 'light',
      pace: 'moderate',
      difficulty: 'easy',
      description_style: 'balanced',
      npc_style: 'friendly',
      combat_narration: 'cinematic',
      stakes: 'low',
      content_balance: 'balanced'
    }
  },
  {
    value: 'epic_adventure',
    label: 'Epic Adventure',
    description: 'Heroic quests with dramatic moments and world-changing stakes',
    settings: {
      tone: 'heroic',
      pace: 'moderate',
      difficulty: 'medium',
      description_style: 'verbose',
      npc_style: 'dramatic',
      combat_narration: 'cinematic',
      stakes: 'high',
      content_balance: 'balanced'
    }
  },
  {
    value: 'dark_souls',
    label: 'Dark & Deadly',
    description: 'Brutal, unforgiving world where death lurks around every corner',
    settings: {
      tone: 'dark',
      pace: 'moderate',
      difficulty: 'deadly',
      description_style: 'terse',
      npc_style: 'reserved',
      combat_narration: 'gritty',
      stakes: 'extreme',
      content_balance: 'combat_heavy'
    }
  },
  {
    value: 'political_intrigue',
    label: 'Political Intrigue',
    description: 'Scheming nobles, hidden agendas, and social maneuvering',
    settings: {
      tone: 'dark',
      pace: 'slow',
      difficulty: 'medium',
      description_style: 'verbose',
      npc_style: 'reserved',
      combat_narration: 'tactical',
      stakes: 'high',
      content_balance: 'roleplay_heavy'
    }
  },
  {
    value: 'dungeon_crawl',
    label: 'Dungeon Crawl',
    description: 'Classic exploration with traps, treasures, and tactical combat',
    settings: {
      tone: 'balanced',
      pace: 'fast',
      difficulty: 'hard',
      description_style: 'terse',
      npc_style: 'reserved',
      combat_narration: 'tactical',
      stakes: 'moderate',
      content_balance: 'exploration_heavy'
    }
  },
  {
    value: 'action_movie',
    label: 'Action Movie',
    description: 'Fast-paced thrills with explosive combat and dramatic flair',
    settings: {
      tone: 'heroic',
      pace: 'fast',
      difficulty: 'easy',
      description_style: 'terse',
      npc_style: 'dramatic',
      combat_narration: 'cinematic',
      stakes: 'moderate',
      content_balance: 'combat_heavy'
    }
  }
];

const DEFAULT_PRESET_LOOKUP = DEFAULT_PRESET_LIST.reduce((acc, preset) => {
  acc[preset.value] = preset.settings;
  return acc;
}, {});

const DEFAULT_PERSONALITY_OPTIONS = {
  tone: [
    { value: 'light', label: 'Light', description: 'Comedic, casual, low-stakes fun with quirky NPCs' },
    { value: 'balanced', label: 'Balanced', description: 'Mix of humor and drama, realistic motivations' },
    { value: 'dark', label: 'Dark', description: 'Gritty, moral ambiguity, serious consequences' },
    { value: 'heroic', label: 'Heroic', description: 'Epic, legendary actions, world-changing stakes' }
  ],
  pace: [
    { value: 'slow', label: 'Slow', description: 'Methodical, atmospheric, builds tension gradually' },
    { value: 'moderate', label: 'Moderate', description: 'Natural flow, balanced scene-setting' },
    { value: 'fast', label: 'Fast', description: 'Quick transitions, action-packed, urgent' }
  ],
  description_style: [
    { value: 'terse', label: 'Terse', description: 'Minimal details, punchy sentences' },
    { value: 'balanced', label: 'Balanced', description: 'Moderate detail, highlights important elements' },
    { value: 'verbose', label: 'Verbose', description: 'Rich, immersive, multiple senses' }
  ],
  npc_style: [
    { value: 'reserved', label: 'Reserved', description: 'Guarded, trust is earned, subtle hints' },
    { value: 'friendly', label: 'Friendly', description: 'Helpful, open, approachable' },
    { value: 'dramatic', label: 'Dramatic', description: 'Big personalities, memorable quirks' }
  ],
  combat_narration: [
    { value: 'tactical', label: 'Tactical', description: 'Focus on positioning and strategy' },
    { value: 'cinematic', label: 'Cinematic', description: 'Movie-like action, dramatic moments' },
    { value: 'gritty', label: 'Gritty', description: 'Brutal, visceral, lasting consequences' }
  ],
  stakes: [
    { value: 'low', label: 'Low', description: 'Relaxed, minor consequences, fun over challenge' },
    { value: 'moderate', label: 'Moderate', description: 'Balanced risk/reward, success feels earned' },
    { value: 'high', label: 'High', description: 'Significant consequences, real danger' },
    { value: 'extreme', label: 'Extreme', description: 'Life-or-death, world-altering outcomes' }
  ],
  content_balance: [
    { value: 'combat_heavy', label: 'Combat-Heavy', description: 'Focus on tactical battles and combat' },
    { value: 'balanced', label: 'Balanced', description: 'Mix of combat, roleplay, and exploration' },
    { value: 'roleplay_heavy', label: 'Roleplay-Heavy', description: 'Focus on character interactions' },
    { value: 'exploration_heavy', label: 'Exploration', description: 'Focus on discovery and puzzles' }
  ]
};

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy', description: 'Forgiving encounters, room for mistakes' },
  { value: 'medium', label: 'Medium', description: 'Balanced challenge, fair but engaging' },
  { value: 'hard', label: 'Hard', description: 'Challenging encounters, requires strategy' },
  { value: 'deadly', label: 'Deadly', description: 'Brutal difficulty, death is common' }
];

export const DRAFT_STORAGE_KEY = 'gaia_campaign_setup_draft';

const normalizeCampaignIdentifier = (value) => (
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
);

const campaignIdentifierMatches = (left, right) => {
  const leftNorm = normalizeCampaignIdentifier(left);
  const rightNorm = normalizeCampaignIdentifier(right);
  if (!leftNorm || !rightNorm) {
    return false;
  }
  return leftNorm === rightNorm;
};

const buildInitialCampaignInfo = () => ({
  campaign_id: '',
  title: '',
  description: '',
  setting: '',
  theme: '',
  starting_location: '',
  main_conflict: '',
  starting_npcs: [],
  acts: [],  // Array of {act_number, title, goals, central_conflict, stakes, setting, resolution, ...}
  campaign_resolution: '',
  // DM Personality settings
  campaign_style: DEFAULT_CAMPAIGN_STYLE, // Preset selector
  ...DEFAULT_PRESET_LOOKUP[DEFAULT_CAMPAIGN_STYLE]
});

const CampaignSetup = ({
  isOpen,
  onComplete,
  onCancel,
  onCreateBlank,
  prefillCharacterId = null,
  prefillPregeneratedCampaignId = null,
  forceSinglePlayer = false
}) => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;
  const navigate = useNavigate();

  // Mobile breakpoint — controls full-screen mode, stepper, and contextual CTA
  const isMobile = useMobileBreakpoint();

  const [step, setStep] = useState(1); // 1: Campaign Info, 3: Character Creation
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Campaign info - includes full data from auto-generation
  const [campaignInfo, setCampaignInfo] = useState(buildInitialCampaignInfo);

  const [personalityOptions, setPersonalityOptions] = useState({
    presets: DEFAULT_PRESET_LIST,
    options: DEFAULT_PERSONALITY_OPTIONS
  });

  // Build a lookup for default descriptions by preset value
  const defaultDescriptionLookup = DEFAULT_PRESET_LIST.reduce((acc, preset) => {
    acc[preset.value] = preset.description;
    return acc;
  }, {});

  // Merge descriptions from defaults into API presets (API presets may not have descriptions)
  const personalityPresetOptions = (personalityOptions?.presets?.length
    ? personalityOptions.presets
    : DEFAULT_PRESET_LIST
  ).map(preset => ({
    ...preset,
    description: preset.description || defaultDescriptionLookup[preset.value] || ''
  }));

  const campaignStyleOptions = personalityPresetOptions.some((preset) => preset.value === 'custom')
    ? personalityPresetOptions
    : [...personalityPresetOptions, { value: 'custom', label: 'Custom...', description: 'Build your own style with custom settings' }];

  const personalityOptionSets = {
    ...DEFAULT_PERSONALITY_OPTIONS,
    ...(personalityOptions?.options || {})
  };

  const presetLookup = personalityPresetOptions.reduce((acc, preset) => {
    const settings = { ...(preset.settings || {}) };
    if (!settings.difficulty && DEFAULT_PRESET_DIFFICULTY[preset.value]) {
      settings.difficulty = DEFAULT_PRESET_DIFFICULTY[preset.value];
    }
    acc[preset.value] = settings;
    return acc;
  }, {});

  // Apply preset when campaign_style changes
  const applyCampaignStylePreset = (presetKey) => {
    const preset = presetLookup[presetKey];
    if (preset) {
      setCampaignInfo(prev => ({
        ...prev,
        campaign_style: presetKey,
        ...preset
      }));
    } else {
      // Custom - just update the selector, keep current values
      setCampaignInfo(prev => ({ ...prev, campaign_style: presetKey }));
    }
  };

  // Track if campaign was pre-generated
  const [campaignWasPregenerated, setCampaignWasPregenerated] = useState(false);

  // Game mode: 'quest_loop' (guided quest cycle) or 'game' (open sandbox)
  const [gameMode, setGameMode] = useState('quest_loop');
  // Admin/testing (BOU-1792): skip Jim + quest-giver preamble and land directly
  // in the quest. Quest Mode only; the backend ignores it for other types.
  const [skipIntro, setSkipIntro] = useState(false);
  // BOU-1907: opt into the cinematic 3D scene diorama backdrop at setup.
  const [sceneDiorama, setSceneDiorama] = useState(false);

  // Campaign setup mode
  const [_setupMode, _setSetupMode] = useState('new'); // new, blank, existing
  const [campaignId, setCampaignId] = useState(null);
  const [campaignExternalId, setCampaignExternalId] = useState(null);

  // Player setup
  const [playerCount, setPlayerCount] = useState(1);
  // characterSlots is the parent's cached view of each slot's full state,
  // updated via CampaignSlotEditor.onSnapshot. Each <CampaignSlotEditor>
  // owns its own draft via useCharacterDraft; this array is the parent's
  // read-mostly aggregate cache used for validation, dropdown filtering,
  // and the finalizeSetup payload build.
  const [characterSlots, setCharacterSlots] = useState([]);
  const [, setAvailableVoices] = useState([]);
  const [pregeneratedCharacters, setPregeneratedCharacters] = useState([]);
  const [pregeneratedCampaigns, setPregeneratedCampaigns] = useState([]);
  const [myCharacters, setMyCharacters] = useState([]);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [pregenPrefillApplied, setPregenPrefillApplied] = useState(false);
  // Per-slot selections recovered from a saved draft, pending re-hydration once
  // the slot editors and their source character lists have mounted. Set when
  // resuming after the "Create Character" leave-flow so a partially assembled
  // multi-player roster is not discarded. Cleared once applied.
  const [pendingSlotSelections, setPendingSlotSelections] = useState(null);

  // Imperative refs to each <CampaignSlotEditor> for aggregate write
  // operations (autoFillAllCharacters, programmatic prefill). Each ref
  // exposes { getSnapshot, hydrateFromSource, clear, setField, setFields }.
  const slotRefs = useRef([]);
  const autoFinalizeTriggeredRef = useRef(false);

  // Flips true the instant the user clicks a character tile that completes
  // the roster, so the picker grid is replaced with a loading view before
  // the async snapshot → setCharacterSlots → finalizeSetup chain has a
  // chance to land setLoading(true). Without this the picker stays visible
  // through the multi-second initializeCampaign call.
  const [pickStarted, setPickStarted] = useState(false);
  const [incompleteCharacterActions, setIncompleteCharacterActions] = useState([]);

  // Snapshot handler: each <CampaignSlotEditor> calls onSnapshot whenever
  // its internal state changes (draft, selection, modification flags). The
  // parent caches the snapshot into characterSlots in the shape the legacy
  // aggregate code expects (slot_id, seat_id, all draft fields plus the
  // tracking flags). finalizeSetup, validation, and getSelectedCharacterIds
  // all read this view.
  const handleSlotSnapshot = useCallback((snapshotSlotId, snapshot) => {
    setCharacterSlots((prev) => prev.map((slot) => (
      slot.slot_id === snapshotSlotId
        ? {
          ...slot,
          ...snapshot.draft,
          selection: snapshot.selection,
          existing_character_id: snapshot.existingCharacterId,
          was_pregenerated: snapshot.wasPregenerated,
          is_modified: snapshot.isModified,
          is_filled: snapshot.isFilled,
        }
        : slot
    )));
  }, []);

  // Reset the auto-finalize latch whenever step 2 mounts (or remounts after
  // an unrelated wizard step change) so a follow-up flow can fire it again.
  useEffect(() => {
    if (step !== 3) {
      autoFinalizeTriggeredRef.current = false;
      setPickStarted(false);
    }
  }, [step]);

  // Synchronous tile-click signal from each <CampaignSlotEditor>. If this
  // click lands on the slot that completes the roster (every other slot is
  // already filled), flip pickStarted so the loading view appears
  // immediately — well before the snapshot round-trip triggers
  // finalizeSetup/setLoading.
  const handleSlotPicked = useCallback((pickedSlotId) => {
    if (step !== 3) return;
    const otherSlotsFilled = characterSlots.every((slot) => (
      slot.slot_id === pickedSlotId || slot.is_filled
    ));
    if (otherSlotsFilled) {
      setPickStarted(true);
    }
  }, [step, characterSlots]);

  const handleCreateCharacter = useCallback(() => {
    const setupMode = forceSinglePlayer ? 'solo' : 'campaign';
    navigate(`/characters/new?from=campaign-setup&setupMode=${setupMode}`);
  }, [forceSinglePlayer, navigate]);

  const handleEditIncompleteCharacter = useCallback((slot) => {
    const setupMode = forceSinglePlayer ? 'solo' : 'campaign';
    if (slot.characterId) {
      navigate(
        `/characters/${encodeURIComponent(slot.characterId)}?from=campaign-setup&setupMode=${setupMode}`,
      );
      return;
    }
    navigate(`/characters/new?from=campaign-setup&setupMode=${setupMode}`);
  }, [forceSinglePlayer, navigate]);

  // Helper to get character identifiers already selected in other slots.
  // Sources from the parent's cached characterSlots view (kept in sync by
  // CampaignSlotEditor.onSnapshot). Each slot's `selection` is the dropdown
  // value; `was_pregenerated + name` covers random-fill (where dropdown is
  // cleared but the pregen name is still claimed).
  const getSelectedCharacterIds = (excludeSlotId = null) => {
    const selectedPregenNames = new Set();
    const selectedMyCharIds = new Set();
    characterSlots.forEach((slot) => {
      if (excludeSlotId !== null && slot.slot_id === excludeSlotId) return;
      if (slot.selection?.type === 'pregen' && slot.selection.value) {
        selectedPregenNames.add(slot.selection.value);
      }
      if (slot.selection?.type === 'my' && slot.selection.value) {
        selectedMyCharIds.add(slot.selection.value);
      }
      // Random-fill case: dropdown cleared, but pregen name still in use.
      if (slot.name && slot.was_pregenerated) {
        selectedPregenNames.add(slot.name);
      }
      if (slot.existing_character_id) {
        selectedMyCharIds.add(slot.existing_character_id);
      }
    });
    return { pregenNames: selectedPregenNames, myCharIds: selectedMyCharIds };
  };

  const effectivePlayerCount = forceSinglePlayer ? 1 : playerCount;

  const normalizePlayerCount = (count) => {
    if (forceSinglePlayer) {
      return 1;
    }
    const parsedCount = Number(count);
    if (!Number.isFinite(parsedCount)) {
      return 2;
    }
    return Math.min(4, Math.max(1, Math.trunc(parsedCount)));
  };

  const buildEmptyCharacterSlots = (count, seatIdMap = {}, existingSlots = []) => (
    Array.from({ length: count || playerCount }, (_, index) => {
      const existing = existingSlots[index] || {};
      return {
        name: '',
        character_class: '',
        race: '',
        level: 1,
        voice_id: '',
        description: '',
        backstory: '',
        is_filled: false,
        ...existing,
        slot_id: index,
        seat_id: seatIdMap[index] || existing.seat_id || null,
      };
    })
  );

  // Load available voices, pregenerated characters/campaigns, and user's characters
  useEffect(() => {
    if (!isOpen) return;
    loadAvailableVoices();
    loadPregeneratedCharacters();
    loadPregeneratedCampaigns();
    loadMyCharacters();
    loadPersonalityOptions();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!prefillCharacterId || prefillApplied) return;
    if (!characterSlots.length || !myCharacters.length) return;
    const firstSlot = characterSlots[0];
    if (!firstSlot) return;
    const target = myCharacters.find((character) => character.character_id === prefillCharacterId);
    if (!target) return;
    // Use the slot editor's imperative API; mirrors what `my:<id>` selection
    // would have done before the slot editor extraction.
    slotRefs.current[0]?.hydrateFromSource(target, {
      wasPregenerated: false,
      existingCharacterId: target.character_id,
      selection: { type: 'my', value: target.character_id },
    });
    setPrefillApplied(true);
  }, [isOpen, prefillCharacterId, prefillApplied, characterSlots, myCharacters]);

  // Restore previously picked slot selections after resuming from the "Create
  // Character" leave-flow. Selections are reconstructed from the source
  // character lists (the same way handleMyCharSelect/handlePregenSelect do it)
  // and applied through each slot editor's imperative hydrateFromSource. Waits
  // until the slots are rebuilt and the relevant source list has loaded so a
  // selection is never dropped for a not-yet-loaded character.
  useEffect(() => {
    if (!isOpen || !pendingSlotSelections) return;
    if (!characterSlots.length) return;
    const needsMy = pendingSlotSelections.some((sel) => sel?.type === 'my');
    const needsPregen = pendingSlotSelections.some((sel) => sel?.type === 'pregen');
    if (needsMy && !myCharacters.length) return;
    if (needsPregen && !pregeneratedCharacters.length) return;

    pendingSlotSelections.forEach((sel, index) => {
      if (!sel?.type) return;
      const slotRef = slotRefs.current[index];
      if (!slotRef) return;
      if (sel.type === 'my') {
        const character = myCharacters.find((c) => c.character_id === sel.value);
        if (character) {
          slotRef.hydrateFromSource(character, {
            selection: sel,
            existingCharacterId: character.character_id,
          });
        }
      } else if (sel.type === 'pregen') {
        const character = pregeneratedCharacters.find((c) => c.name === sel.value);
        if (character) {
          slotRef.hydrateFromSource(character, {
            selection: sel,
            wasPregenerated: true,
          });
        }
      }
    });
    setPendingSlotSelections(null);
  }, [isOpen, pendingSlotSelections, characterSlots, myCharacters, pregeneratedCharacters]);

  useEffect(() => {
    if (!isOpen) return;
    if (!prefillPregeneratedCampaignId || pregenPrefillApplied) return;
    if (!pregeneratedCampaigns.length) return;
    selectPregeneratedCampaign(prefillPregeneratedCampaignId);
    setPregenPrefillApplied(true);
  }, [isOpen, prefillPregeneratedCampaignId, pregenPrefillApplied, pregeneratedCampaigns]);

  useEffect(() => {
    if (!isOpen) return;
    if (!forceSinglePlayer) return;
    if (playerCount !== 1) {
      setPlayerCount(1);
    }
  }, [isOpen, forceSinglePlayer, playerCount]);

  // Load draft from localStorage when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadDraft = async () => {
      try {
        // Always reset to a clean baseline when opening the modal to avoid stale in-memory values
        // from previous sessions (for example, older non-string campaignInfo.setting payloads).
        setCampaignInfo(buildInitialCampaignInfo());
        setCampaignWasPregenerated(false);
        setPlayerCount(1);
        setPrefillApplied(false);
        setPregenPrefillApplied(false);
        setPendingSlotSelections(null);
        // BOU-1792: the component stays mounted across isOpen toggles, so the
        // admin skip-intro opt-in must reset per setup session — otherwise a
        // prior check (or cancel) could silently bypass intro on the next campaign.
        setSkipIntro(false);
        setSceneDiorama(false);

        // If we have a prefill intent, start fresh at step 1 (clear any existing draft)
        if (prefillCharacterId || prefillPregeneratedCampaignId) {
          console.log('[CampaignSetup] Starting fresh with prefill intent, clearing draft');
          localStorage.removeItem(DRAFT_STORAGE_KEY);
          setStep(1);
          setCampaignId(null);
          setCampaignExternalId(null);
          return;
        }

        const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (savedDraft) {
          const draft = JSON.parse(savedDraft);
          console.log('[CampaignSetup] Loaded draft from localStorage:', draft);

          // Restore campaign info (text fields)
          if (draft.campaignInfo) {
            const draftSetting = draft.campaignInfo.setting;
            setCampaignInfo(prev => ({
              ...prev,
              title: draft.campaignInfo.title || '',
              description: draft.campaignInfo.description || '',
              setting: typeof draftSetting === 'string' ? draftSetting : '',
            }));
          }

          if (!forceSinglePlayer && isAdmin && draft.playerCount) {
            setPlayerCount(draft.playerCount);
          }

          // Restore DM settings (these should also be in localStorage as fallback)
          if (draft.dmSettings) {
            setCampaignInfo(prev => ({
              ...prev,
              campaign_style: draft.dmSettings.campaign_style || DEFAULT_CAMPAIGN_STYLE,
              tone: draft.dmSettings.tone,
              pace: draft.dmSettings.pace,
              difficulty: draft.dmSettings.difficulty,
              description_style: draft.dmSettings.description_style,
              npc_style: draft.dmSettings.npc_style,
              combat_narration: draft.dmSettings.combat_narration,
              stakes: draft.dmSettings.stakes,
              content_balance: draft.dmSettings.content_balance,
            }));
          }

          // Restore campaign ID and step if campaign was already created
          // This prevents creating duplicate campaigns on resume
          if (draft.campaignId && draft.step >= 3) {
            console.log('[CampaignSetup] Resuming campaign:', draft.campaignId, 'at step:', draft.step);
            setCampaignId(draft.campaignId);
            setCampaignExternalId(draft.campaignExternalId || null);
            setStep(draft.step);
            // Reload character slots for the existing campaign
            await loadCharacterSetup(
              draft.campaignId,
              forceSinglePlayer || !isAdmin ? 1 : (draft.playerCount || 1)
            );
            // Queue any previously picked slot selections for re-hydration once
            // the editors and their source character lists are available.
            if (Array.isArray(draft.slotSelections)
                && draft.slotSelections.some((sel) => sel?.type)) {
              setPendingSlotSelections(draft.slotSelections);
            }
          }
        }
      } catch (err) {
        console.warn('[CampaignSetup] Failed to load draft:', err);
      }
    };

    loadDraft();
  }, [isOpen, prefillCharacterId, prefillPregeneratedCampaignId, forceSinglePlayer, isAdmin]);

  // Save draft to localStorage whenever relevant state changes
  useEffect(() => {
    if (!isOpen) return;

    const draft = {
      campaignInfo: {
        title: campaignInfo.title,
        description: campaignInfo.description,
        setting: typeof campaignInfo.setting === 'string' ? campaignInfo.setting : '',
      },
      playerCount: effectivePlayerCount,
      dmSettings: {
        campaign_style: campaignInfo.campaign_style,
        tone: campaignInfo.tone,
        pace: campaignInfo.pace,
        difficulty: campaignInfo.difficulty,
        description_style: campaignInfo.description_style,
        npc_style: campaignInfo.npc_style,
        combat_narration: campaignInfo.combat_narration,
        stakes: campaignInfo.stakes,
        content_balance: campaignInfo.content_balance,
      },
      // Track campaign creation progress to avoid duplicates on resume
      campaignId: campaignId || null,
      campaignExternalId: campaignExternalId || null,
      step,
      // Persist each slot's picked selection so a partially assembled roster
      // survives the round-trip to /characters/new (the editors only persist to
      // the backend on finalize). Re-hydrated from the source character lists on
      // resume — see the pendingSlotSelections effect.
      slotSelections: characterSlots.map((slot) => slot.selection ?? { type: null, value: null }),
      savedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch (err) {
      console.warn('[CampaignSetup] Failed to save draft:', err);
    }
  }, [isOpen, campaignInfo, effectivePlayerCount, campaignId, campaignExternalId, step, characterSlots]);

  // Clear draft when setup completes successfully
  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      console.log('[CampaignSetup] Draft cleared');
    } catch (err) {
      console.warn('[CampaignSetup] Failed to clear draft:', err);
    }
  };

  const loadPersonalityOptions = async () => {
    try {
      const data = await apiService.getCampaignPersonalityOptions();
      if (!data) return;
      const presets = Array.isArray(data.presets) ? data.presets : [];
      const options = data.options && typeof data.options === 'object' ? data.options : {};
      setPersonalityOptions({
        presets: presets.length ? presets : DEFAULT_PRESET_LIST,
        options: {
          ...DEFAULT_PERSONALITY_OPTIONS,
          ...options
        }
      });
    } catch (error) {
      console.warn('Could not load campaign personality options:', error.message);
    }
  };

  const loadAvailableVoices = async () => {
    try {
      const data = await apiService.getTTSVoices();
      if (data.voices) {
        setAvailableVoices(data.voices);
      }
    } catch (error) {
      console.error('Error loading voices:', error);
    }
  };

  const loadPregeneratedCharacters = async () => {
    try {
      const data = await apiService.getPregeneratedCharacters();
      console.log('Loaded pregenerated characters:', data);
      if (data.characters) {
        setPregeneratedCharacters(data.characters);
        console.log('Set pregenerated characters count:', data.characters.length);
      }
    } catch (error) {
      console.error('Error loading pregenerated characters:', error);
    }
  };

  const loadPregeneratedCampaigns = async () => {
    try {
      const data = await apiService.getPregeneratedCampaigns();
      console.log('Loaded pregenerated campaigns:', data);
      if (data.campaigns) {
        setPregeneratedCampaigns(data.campaigns);
        console.log('Set pregenerated campaigns count:', data.campaigns.length);
      }
    } catch (error) {
      console.error('Error loading pregenerated campaigns:', error);
    }
  };

  const loadMyCharacters = async () => {
    try {
      const characters = await apiService.getMyCharacters({ characterType: 'player' });
      console.log('Loaded my characters:', characters);
      setMyCharacters(characters || []);
    } catch (error) {
      // Non-fatal - user may not have any characters yet or may not be logged in
      console.log('Could not load user characters:', error.message);
      setMyCharacters([]);
    }
  };

  // Step 1: Create Campaign
  const createCampaign = async (count = effectivePlayerCount) => {
    setLoading(true);
    setError('');

    // Validate required fields before sending
    const campaignName = campaignInfo.title?.trim();
    if (!campaignName) {
      setError('Please enter a campaign name');
      setLoading(false);
      return;
    }

    try {
      const normalizedPlayerCount = normalizePlayerCount(count);
      const normalizedSetting =
        typeof campaignInfo.setting === 'string' && campaignInfo.setting.trim()
          ? campaignInfo.setting.trim()
          : null;

      // Build request payload
      const requestPayload = {
        name: campaignName,
        description: campaignInfo.description || '',
        player_count: normalizedPlayerCount,
        // Full campaign info for LLM generation (only used if not pregenerated)
        campaign_info: campaignWasPregenerated ? null : {
          title: campaignName,
          description: campaignInfo.description || '',
          theme: campaignInfo.theme || null,
          setting: normalizedSetting,
          tone: campaignInfo.tone || null,
          stakes: campaignInfo.stakes || null,
          pace: campaignInfo.pace || null,
          difficulty: campaignInfo.difficulty || null,
        },
        // Skip LLM generation if user selected a pregenerated campaign
        use_pregenerated: campaignWasPregenerated,
        // Pregen id so the backend can seed acts/quests/encounters at create
        // time — the board needs structure regardless of which start flow fires.
        pregenerated_campaign_id: campaignWasPregenerated
          ? (campaignInfo.campaign_id || null)
          : null,
        campaign_type: gameMode,
      };

      console.log('[CampaignSetup] Creating campaign with payload:', requestPayload);

      const data = await apiService.createCampaign(requestPayload);

      const createdCampaignId = data.campaign_id || data.id;
      const externalId = data.external_campaign_id || data.external_id || null;

      if (createdCampaignId) {
        setCampaignId(createdCampaignId);
        setCampaignExternalId(externalId);
        setCharacterSlots(buildEmptyCharacterSlots(normalizedPlayerCount));
        setStep(3);

        // Persist settings and hydrate seat IDs without blocking the move to
        // character selection. Final setup saves settings again, and backend
        // initialization can fall back to slot indexes if seat IDs are not ready.
        apiService.updateCampaignSettings(createdCampaignId, {
          tone: campaignInfo.tone,
          pace: campaignInfo.pace,
          difficulty: campaignInfo.difficulty,
          description_style: campaignInfo.description_style,
          npc_style: campaignInfo.npc_style,
          combat_narration: campaignInfo.combat_narration,
          stakes: campaignInfo.stakes,
          content_balance: campaignInfo.content_balance,
          enable_scene_diorama: sceneDiorama,
        })
          .then(() => {
            console.log('✅ Campaign settings saved immediately after creation');
          })
          .catch((settingsError) => {
            console.error('Warning: Failed to save campaign settings:', settingsError);
          });

        loadCharacterSetup(createdCampaignId, normalizedPlayerCount);
      } else {
        Error('Failed to create campaign');
      }
    } catch (error) {
      console.error('[CampaignSetup] Error creating campaign:', error);
      console.error('[CampaignSetup] Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      const errorMsg = error.message || String(error);
      setError('Error creating campaign: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill all characters with unique pregenerated characters by walking
  // each slot's imperative editor ref and calling hydrateFromSource. Each
  // Auto-fill campaign with generated data
  const autoFillCampaign = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await apiService.generateCampaign({});

      if (data.success && data.campaign) {
        const campaignSetting = typeof data.campaign.setting === 'string' ? data.campaign.setting : '';
        setCampaignInfo(prev => ({
          ...prev,
          campaign_id: data.campaign.campaign_id || data.campaign.pregen_id || '',
          title: data.campaign.name || data.campaign.title || '',
          description: data.campaign.description || '',
          setting: campaignSetting,
          theme: data.campaign.theme || '',
          starting_location: data.campaign.starting_location || '',
          main_conflict: data.campaign.main_conflict || '',
          starting_npcs: data.campaign.starting_npcs || [],
          acts: data.campaign.acts || [],
          campaign_resolution: data.campaign.campaign_resolution || '',
        }));
        // Mark campaign as pre-generated
        setCampaignWasPregenerated(true);
      } else {
        setError('Failed to generate campaign');
      }
    } catch (error) {
      console.error('Error generating campaign:', error);
      setError('Error generating campaign: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Select a pregenerated campaign from the picker
  const selectPregeneratedCampaign = (campaignId) => {
    if (!campaignId) return;

    const normalizedTargetId = normalizeCampaignIdentifier(campaignId);
    const selectedCampaign = pregeneratedCampaigns.find(
      c => c.campaign_id === campaignId
        || c.pregen_id === campaignId
        || c.id === campaignId
        || c.title === campaignId
        || c.name === campaignId
        || (normalizedTargetId && (
          campaignIdentifierMatches(c.title, normalizedTargetId)
          || campaignIdentifierMatches(c.name, normalizedTargetId)
        ))
    );

    if (selectedCampaign) {
      const campaignSetting = typeof selectedCampaign.setting === 'string' ? selectedCampaign.setting : '';
      setCampaignInfo(prev => ({
        ...prev,
        campaign_id: selectedCampaign.campaign_id || selectedCampaign.pregen_id || selectedCampaign.id || '',
        title: selectedCampaign.name || selectedCampaign.title || '',
        description: selectedCampaign.description || '',
        setting: campaignSetting,
        theme: selectedCampaign.theme || '',
        starting_location: selectedCampaign.starting_location || '',
        main_conflict: selectedCampaign.main_conflict || '',
        starting_npcs: selectedCampaign.starting_npcs || [],
        acts: selectedCampaign.acts || [],
        campaign_resolution: selectedCampaign.campaign_resolution || '',
      }));
      // Mark campaign as pre-generated
      setCampaignWasPregenerated(true);
    }
  };

  const handlePregeneratedCampaignSelect = (campaignId) => {
    if (!campaignId) {
      setCampaignInfo(prev => ({
        ...prev,
        campaign_id: ''
      }));
      setCampaignWasPregenerated(false);
      return;
    }

    selectPregeneratedCampaign(campaignId);
  };

  // Load character setup slots
  const loadCharacterSetup = async (campaignId, count) => {
    try {
      let seatIdMap = {};
      try {
        const seatResponse = await apiService.listSeats(campaignId);
        const playerSeats = seatResponse?.seats?.filter((seat) => seat.seat_type === 'player') || [];
        playerSeats.forEach((seat) => {
          if (seat.slot_index !== undefined && seat.slot_index !== null) {
            seatIdMap[seat.slot_index] = seat.seat_id;
          }
        });
      } catch (seatError) {
        console.warn('Failed to fetch room seats for campaign setup:', seatError);
      }

      setCharacterSlots((existingSlots) => (
        buildEmptyCharacterSlots(count || playerCount, seatIdMap, existingSlots)
      ));
    } catch (error) {
      console.error('Error loading character setup:', error);
    }
  };


  // Save character slot
  const _saveCharacterSlot = async (slotId) => {
    const slot = characterSlots.find(s => s.slot_id === slotId);
    if (!slot) return;

    try {
      const data = await apiService.assignCharacterToSlot(campaignId, slot);
      if (data.updated) {
        console.log('Character slot saved');
      }
    } catch (error) {
      console.error('Error saving character slot:', error);
    }
  };

  // Finalize campaign setup using backend initialization
  const finalizeSetup = async () => {
    setLoading(true);
    // Clear any previous errors
    setError('');
    setIncompleteCharacterActions([]);

    // Refresh each slot directly from its editor ref before validating /
    // building the payload — the parent's cached characterSlots can lag by
    // one render after a field edit if the user clicks Start Campaign
    // immediately, since the editor's onSnapshot fires in a useEffect.
    const liveSlots = characterSlots.map((slot, i) => {
      const snap = slotRefs.current[i]?.getSnapshot();
      if (!snap) return slot;
      return {
        ...slot,
        ...snap.draft,
        selection: snap.selection,
        existing_character_id: snap.existingCharacterId,
        was_pregenerated: snap.wasPregenerated,
        is_modified: snap.isModified,
        is_filled: snap.isFilled,
      };
    });

    // Use centralized validation
    const validation = getIncompleteSlotsValidation(liveSlots, {
      requireNamedCharacter: forceSinglePlayer
    });

    if (!validation.isValid) {
      if (validation.missingRequiredCharacter) {
        setError('Please create your character before starting this solo campaign.');
        setLoading(false);
        setPickStarted(false);
        // Release the auto-finalize latch so picking a complete character retries.
        autoFinalizeTriggeredRef.current = false;
        return;
      }
      const errorMessage = `Cannot start campaign. Please complete the following characters:\n${
        validation.incompleteSlots.map(slot =>
          `${slot.name} (missing: ${slot.missingFields.join(', ')})`
        ).join('\n')
      }`;
      console.error(errorMessage);
      setError(errorMessage);
      setIncompleteCharacterActions(validation.incompleteSlots);
      setLoading(false);
      setPickStarted(false);
      // Release the auto-finalize latch so picking a complete character retries.
      autoFinalizeTriggeredRef.current = false;
      return;
    }

    try {
      const defaultVisualValues = {
        gender: 'non-binary',
        facial_expression: 'determined',
        build: 'average'
      };

      // Build character slots array with per-slot configuration
      // Only include slots that have names defined
      const character_slots = liveSlots.map(slot => {
        const isFilled = slot.name && slot.name.trim() !== '';
        const usePregen = slot.was_pregenerated && !slot.is_modified;
        const isExistingChar = Boolean(slot.existing_character_id);
        console.log(`Slot ${slot.slot_id}: was_pregenerated=${slot.was_pregenerated}, is_modified=${slot.is_modified}, use_pregenerated=${usePregen}, existing_character_id=${slot.existing_character_id || 'none'}`);
        const normalizedGender = slot.gender || defaultVisualValues.gender;
        const normalizedExpression = slot.facial_expression || defaultVisualValues.facial_expression;
        const normalizedBuild = slot.build || defaultVisualValues.build;
        return {
          slot_id: slot.slot_id,
          seat_id: slot.seat_id,
          // A character uses pre-generated data only if:
          // 1. It was originally pre-generated (was_pregenerated flag)
          // 2. It hasn't been modified by the user (no is_modified flag)
          // 3. It's NOT an existing character being reused
          use_pregenerated: usePregen && !isExistingChar,
          // If user selected an existing character, pass its ID for reuse
          existing_character_id: slot.existing_character_id || null,
          // Reused existing profiles are identified by existing_character_id.
          // Avoid sending setup defaults back through the profile update path.
          character_data: isFilled && !isExistingChar ? {
            name: slot.name,
            character_class: slot.character_class,
            race: slot.race,
            level: slot.level,
            description: slot.description,
            backstory: slot.backstory,
            voice_id: slot.voice_id,
            // Visual metadata for portrait generation
            gender: normalizedGender,
            age_category: slot.age_category,
            build: normalizedBuild,
            height_description: slot.height_description,
            facial_expression: normalizedExpression,
            facial_features: slot.facial_features,
            attire: slot.attire,
            primary_weapon: slot.primary_weapon,
            distinguishing_feature: slot.distinguishing_feature,
            background_setting: slot.background_setting,
            pose: slot.pose,
            // Portrait data (required). ID-only write — backend resolution is
            // unscoped so the same id resolves for campaign init alike.
            ...buildCharacterImagePayloadForWrite(slot)
          } : null
        };
      });

      console.log(`Campaign: was_pregenerated=${campaignWasPregenerated}`);

      // Call the backend to initialize the campaign with all context
      const result = await apiService.initializeCampaign({
        campaign_id: campaignId,
        campaign_info: campaignInfo,
        use_pregenerated_campaign: campaignWasPregenerated,  // True only if auto-generated and not edited
        character_slots,
        // BOU-1792: admin-only quick-start. Only meaningful for Quest Mode.
        skip_intro: isAdmin && gameMode === 'quest_loop' && skipIntro
      });

      if (result.success) {
        // Save campaign settings (DM personality configuration)
        try {
          await apiService.updateCampaignSettings(campaignId, {
            tone: campaignInfo.tone,
            pace: campaignInfo.pace,
            difficulty: campaignInfo.difficulty,
            description_style: campaignInfo.description_style,
            npc_style: campaignInfo.npc_style,
            combat_narration: campaignInfo.combat_narration,
            stakes: campaignInfo.stakes,
            content_balance: campaignInfo.content_balance,
            enable_scene_diorama: sceneDiorama
          });
          console.log('✅ Campaign settings saved');
        } catch (settingsError) {
          console.error('Warning: Failed to save campaign settings:', settingsError);
          // Don't fail the whole flow if settings save fails
        }

        // BOU-920: trigger Stage Cutout generation for each newly-created
        // character via the same helper /characters route uses post-save.
        // Reused-existing characters already have their cutout pipeline.
        const slotsBySlotId = Object.fromEntries(
          characterSlots.map((s) => [s.slot_id, s])
        );
        queueStageCutoutForCharacters(result.characters || [], slotsBySlotId);

        console.log('✅ Campaign initialized in setup state. Use Room Management to start when ready.');
        // Clear the localStorage draft since setup completed successfully
        clearDraft();
        if (onComplete) {
          onComplete(campaignExternalId || campaignId, { campaignType: gameMode });
        }
      } else {
        throw new Error('Failed to initialize campaign');
      }
    } catch (error) {
      console.error('Error finalizing setup:', error);
      setError('Error finalizing setup: ' + error.message);
      setPickStarted(false);
      autoFinalizeTriggeredRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Auto-finalize: once every slot in step 2 has a character picked, fire
  // finalizeSetup automatically. The user picks a portrait and the campaign
  // starts — no Start Campaign button.
  //
  // Gate ONLY on every slot being filled (is_filled = a character is picked).
  // Do NOT gate on full field validation here: handleSlotPicked already flips
  // pickStarted (the "Starting the adventure…" spinner) the instant a slot is
  // filled, so silently returning on a validation miss would strand that
  // spinner forever with no feedback (BOU-1348 — e.g. picking a character that
  // has no voice_id). Instead always hand off to finalizeSetup, whose own
  // validation surfaces an actionable "missing: …" error and resets the
  // spinner so the user can pick a complete character.
  useEffect(() => {
    if (step !== 3) return;
    if (loading) return;
    if (autoFinalizeTriggeredRef.current) return;
    if (!characterSlots.length) return;
    // Need every slot to actually have a picked character (is_filled true on
    // each slot). allSlotsEmpty=true means nothing picked yet → not ready.
    const everySlotFilled = characterSlots.every((s) => s.is_filled);
    if (!everySlotFilled) return;
    autoFinalizeTriggeredRef.current = true;
    finalizeSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, characterSlots, loading, forceSinglePlayer]);

  // Render campaign info step
  const renderCampaignInfo = () => (
    <div className="campaign-setup-step">
      <div className="step-header">
        <h2>Create New Campaign</h2>
        <div className="step-header-buttons">
          <Button
            onClick={autoFillCampaign}
            disabled={loading}
          >
            🎲 Random
          </Button>
          {pregeneratedCampaigns.length > 0 && (
            <Select
              value={campaignInfo.campaign_id || ''}
              onChange={handlePregeneratedCampaignSelect}
              disabled={loading}
              className="pregen-campaign-select min-w-[200px]"
              forceNative={true}
              options={[
                { value: '', label: 'Pregenerated...' },
                ...pregeneratedCampaigns.map((campaign, index) => ({
                  value: campaign.campaign_id || campaign.pregen_id || campaign.id || campaign.title || `campaign-${index}`,
                  label: campaign.title || campaign.name || `Campaign ${index + 1}`
                }))
              ]}
            />
          )}
          {onCreateBlank && (
            <Button
              onClick={() => {
                onCreateBlank();
                onCancel();
              }}
              variant="secondary"
              disabled={loading}
            >
              📋 Blank Campaign
            </Button>
          )}
        </div>
      </div>

      <div className="campaign-title-row" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label>Campaign Title</label>
          <Input
            type="text"
            value={campaignInfo.title}
            onChange={(e) => {
              setCampaignInfo({ ...campaignInfo, title: e.target.value });
              // Clear pre-generated flag if user modifies
              if (campaignWasPregenerated) {
                setCampaignWasPregenerated(false);
              }
            }}
            placeholder="Enter campaign title..."
            className="campaign-input"
            data-testid="campaign-name-input"
          />
        </div>
        {(isAdmin && !forceSinglePlayer) && (
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Players</label>
            <div className="player-count-inline" style={{ display: 'flex', gap: '0.25rem' }}>
              {[1, 2, 3, 4].map(count => (
                <Button
                  key={count}
                  className="player-count-btn-small"
                  onClick={() => setPlayerCount(count)}
                  variant={playerCount === count ? 'primary' : 'secondary'}
                  style={{
                    minWidth: '2rem',
                    padding: '0.375rem 0.5rem',
                    fontSize: '0.875rem'
                  }}
                >
                  {count}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Description</label>
        <Textarea
          value={campaignInfo.description}
          onChange={(e) => {
            setCampaignInfo({ ...campaignInfo, description: e.target.value });
            // Clear pre-generated flag if user modifies
            if (campaignWasPregenerated) {
              setCampaignWasPregenerated(false);
            }
          }}
          placeholder="Describe your campaign..."
          className="campaign-textarea"
          rows={4}
        />
      </div>

      {/* Game Mode Toggle */}
      <div className="form-group">
        <label>Game Mode</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            onClick={() => setGameMode('quest_loop')}
            variant={gameMode === 'quest_loop' ? 'primary' : 'secondary'}
            style={{ flex: 1, padding: '0.5rem' }}
            data-testid="quest-mode-toggle"
          >
            <div style={{ fontWeight: 600 }}>Quest Mode</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>Guided quests with Jim the Barkeep</div>
          </Button>
          <Button
            onClick={() => setGameMode('game')}
            variant={gameMode === 'game' ? 'primary' : 'secondary'}
            style={{ flex: 1, padding: '0.5rem' }}
          >
            <div style={{ fontWeight: 600 }}>Sandbox Mode</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>Open-world exploration</div>
          </Button>
        </div>
      </div>

      {/* Admin-only quick-start (BOU-1792): skip Jim + quest-giver preamble */}
      {(isAdmin && gameMode === 'quest_loop') && (
        <div className="form-group">
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
            data-testid="skip-intro-checkbox-label"
          >
            <input
              type="checkbox"
              checked={skipIntro}
              onChange={(e) => setSkipIntro(e.target.checked)}
              data-testid="skip-intro-checkbox"
              style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
            />
            <span>
              <span style={{ fontWeight: 600 }}>Skip preamble (admin)</span>
              <span style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block' }}>
                Bypass Jim &amp; the quest-giver — start directly in the quest. For testing.
              </span>
            </span>
          </label>
        </div>
      )}

      {/* BOU-1907: opt into the cinematic 3D scene diorama (dark-launched — only
          shown when the global flag is on). Persisted as enable_scene_diorama. */}
      {API_CONFIG.ENABLE_SCENE_DIORAMA && (
        <div className="form-group">
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
            data-testid="scene-diorama-checkbox-label"
          >
            <input
              type="checkbox"
              checked={sceneDiorama}
              onChange={(e) => setSceneDiorama(e.target.checked)}
              data-testid="scene-diorama-checkbox"
              style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
            />
            <span>
              <span style={{ fontWeight: 600 }}>Cinematic 3D scene</span>
              <span style={{ fontSize: '0.7rem', opacity: 0.7, display: 'block' }}>
                Render scenes as a depth-lit 3D diorama backdrop instead of a flat image (desktop).
              </span>
            </span>
          </label>
        </div>
      )}

      {/* Campaign Style Preset */}
      <div className="form-group">
        <label>Campaign Style</label>
        <Select
          value={campaignInfo.campaign_style}
          onChange={(value) => applyCampaignStylePreset(value)}
          className="campaign-select"
          isInModal={true}
          showOptionDescriptions={true}
          options={campaignStyleOptions}
        />
      </div>

      {/* Advanced DM Personality Settings - show when Custom selected or expanded */}
      <div className="form-group">
        <button
          type="button"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
          className="advanced-settings-toggle"
        >
          <span className={showAdvancedSettings ? 'advanced-settings-toggle__arrow advanced-settings-toggle__arrow--open' : 'advanced-settings-toggle__arrow'}>▶</span>
          Customize Storyteller Settings
        </button>
      </div>

      {(showAdvancedSettings || campaignInfo.campaign_style === 'custom') && (
        <div className="advanced-settings-panel">
          {/* Row 1: Tone, Difficulty, Pacing */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Tone</label>
              <Select
                value={campaignInfo.tone}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, tone: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={personalityOptionSets.tone}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Difficulty</label>
              <Select
                value={campaignInfo.difficulty}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, difficulty: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={DIFFICULTY_OPTIONS}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Pacing</label>
              <Select
                value={campaignInfo.pace}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, pace: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={personalityOptionSets.pace}
              />
            </div>
          </div>

          {/* Row 2: Descriptions, NPCs, Combat */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Descriptions</label>
              <Select
                value={campaignInfo.description_style}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, description_style: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={personalityOptionSets.description_style}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>NPCs</label>
              <Select
                value={campaignInfo.npc_style}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, npc_style: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={personalityOptionSets.npc_style}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Combat</label>
              <Select
                value={campaignInfo.combat_narration}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, combat_narration: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={personalityOptionSets.combat_narration}
              />
            </div>
          </div>

          {/* Row 3: Stakes & Content Balance */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Stakes</label>
              <Select
                value={campaignInfo.stakes}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, stakes: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={personalityOptionSets.stakes}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Content</label>
              <Select
                value={campaignInfo.content_balance}
                onChange={(value) => setCampaignInfo({ ...campaignInfo, content_balance: value, campaign_style: 'custom' })}
                className="campaign-select"
                isInModal={true}
                showOptionDescriptions={true}
                options={personalityOptionSets.content_balance}
              />
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  // Handle moving from Step 1 to Step 3
  // If campaign already exists (user went back), update it; otherwise create new
  const handleNextToCharacterSetup = async () => {
    const targetPlayerCount = forceSinglePlayer ? 1 : playerCount;

    if (campaignId) {
      // Campaign already exists - update it with any changes
      setLoading(true);
      setError('');
      try {
        console.log('[CampaignSetup] Updating existing campaign:', campaignId);

        // Update campaign metadata
        try {
          await apiService.updateCampaign(campaignId, {
            name: campaignInfo.title,
            description: campaignInfo.description,
            player_count: targetPlayerCount,
          });
          console.log('[CampaignSetup] Campaign metadata updated');
        } catch (metaErr) {
          console.error('[CampaignSetup] Failed to update campaign metadata:', metaErr);
          // Continue anyway - the critical data is saved
        }

        // Update campaign settings (DM personality)
        // Only include fields that have values to avoid validation errors
        try {
          const settingsUpdate = {};
          if (campaignInfo.tone) settingsUpdate.tone = campaignInfo.tone;
          if (campaignInfo.pace) settingsUpdate.pace = campaignInfo.pace;
          if (campaignInfo.difficulty) settingsUpdate.difficulty = campaignInfo.difficulty;
          if (campaignInfo.description_style) settingsUpdate.description_style = campaignInfo.description_style;
          if (campaignInfo.npc_style) settingsUpdate.npc_style = campaignInfo.npc_style;
          if (campaignInfo.combat_narration) settingsUpdate.combat_narration = campaignInfo.combat_narration;
          if (campaignInfo.stakes) settingsUpdate.stakes = campaignInfo.stakes;
          if (campaignInfo.content_balance) settingsUpdate.content_balance = campaignInfo.content_balance;

          if (Object.keys(settingsUpdate).length > 0) {
            console.log('[CampaignSetup] Settings to update:', settingsUpdate);
            await apiService.updateCampaignSettings(campaignId, settingsUpdate);
            console.log('[CampaignSetup] Campaign settings updated');
          }
        } catch (settingsErr) {
          console.error('[CampaignSetup] Failed to update campaign settings:', settingsErr);
          // Continue anyway - settings were saved during initial creation
        }

        console.log('✅ Campaign updated');

        // Update character slots if player count changed
        const currentSlotCount = characterSlots.length;
        if (targetPlayerCount !== currentSlotCount) {
          console.log(`[CampaignSetup] Player count changed from ${currentSlotCount} to ${targetPlayerCount}, updating slots`);
          if (targetPlayerCount > currentSlotCount) {
            // Add new empty slots
            const newSlots = Array.from({ length: targetPlayerCount - currentSlotCount }, (_, i) => ({
              slot_id: currentSlotCount + i,
              seat_id: null,
              name: '',
              character_class: '',
              race: '',
              level: 1,
              voice_id: '',
              description: '',
              backstory: '',
              is_filled: false
            }));
            setCharacterSlots([...characterSlots, ...newSlots]);
          } else {
            // Remove slots from the end
            setCharacterSlots(characterSlots.slice(0, targetPlayerCount));
          }
        }

        setStep(3);
      } catch (err) {
        console.error('Error updating campaign:', err);
        const errorMessage = err?.message || err?.detail || (typeof err === 'string' ? err : JSON.stringify(err));
        setError('Error updating campaign: ' + errorMessage);
      } finally {
        setLoading(false);
      }
    } else {
      // No campaign yet - create new one
      createCampaign(targetPlayerCount);
    }
  };

  // Render character creation step
  const renderCharacterCreation = () => {
    const isLoading = pickStarted || loading;
    return (
      <div className="campaign-setup-step character-creation">
        {/* Loading view: shown while finalize is in flight */}
        {isLoading && (
          <div
            className="campaign-setup-loading"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '40vh',
              textAlign: 'center',
              gap: '1rem',
            }}
          >
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-400" />
            <h2>Starting the adventure…</h2>
            <p className="help-text">Building your campaign world. This usually takes a few seconds.</p>
            {error && <div className="error-message">{error}</div>}
          </div>
        )}

        {/* Step header: hidden while loading */}
        {!isLoading && (
          <div className="step-header">
            <div>
              <h2>{forceSinglePlayer ? 'Pick your character' : 'Pick characters'}</h2>
              <p className="help-text">
                {forceSinglePlayer
                  ? 'Tap a portrait to start the adventure.'
                  : 'Tap a portrait per slot — the campaign starts automatically once every slot is filled.'}
              </p>
            </div>
          </div>
        )}

        {/*
          Keep slot editors mounted even while loading so their onSnapshot useEffect
          fires with the updated draft (is_filled=true) in the same render cycle as
          pickStarted. Unmounting them before onSnapshot fires means the parent's
          characterSlots never updates, the auto-finalize effect never triggers, and
          the game stays stuck on "Starting the adventure…" forever.
        */}
        <div className="character-slots" style={{ display: isLoading ? 'none' : undefined }}>
          {characterSlots.map((slot, index) => {
            const { pregenNames: otherPregenNames, myCharIds: otherMyCharIds } =
              getSelectedCharacterIds(slot.slot_id);
            const myCharacterNames = new Set(myCharacters.map((c) => c.name));
            const pregenPool = pregeneratedCharacters.filter(
              (c) => !myCharacterNames.has(c.name),
            );
            return (
              <CampaignSlotEditor
                key={slot.slot_id}
                ref={(el) => { slotRefs.current[index] = el; }}
                slotId={slot.slot_id}
                seatId={slot.seat_id}
                seatIndex={index}
                pregeneratedCharacters={pregenPool}
                myCharacters={myCharacters}
                otherPregenNames={otherPregenNames}
                otherMyCharIds={otherMyCharIds}
                onSnapshot={handleSlotSnapshot}
                onPicked={handleSlotPicked}
                onCreateCharacter={handleCreateCharacter}
              />
            );
          })}
        </div>

        {!isLoading && (
          <div className="button-group" style={{ justifyContent: 'space-between' }}>
            <Button onClick={() => setStep(1)} variant="ghost" disabled={loading}>
              ← Back to Campaign Info
            </Button>
            <Button onClick={onCancel} variant="secondary" disabled={loading}>
              Cancel
            </Button>
          </div>
        )}

        {!isLoading && error && (
          <div className="error-message">
            <div>{error}</div>
            {incompleteCharacterActions.length > 0 && (
              <div className="button-group" style={{ marginTop: '0.75rem', justifyContent: 'flex-start' }}>
                {incompleteCharacterActions.map((slot) => (
                  <Button
                    key={slot.characterId || slot.name}
                    type="button"
                    variant="primary"
                    onClick={() => handleEditIncompleteCharacter(slot)}
                  >
                    Edit {slot.name}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Shared inner content ────────────────────────────────────────────────
  // The stepper, form content, and footer are identical whether we're in
  // Modal (desktop) or full-screen (mobile) mode. Extract them so both
  // render paths share the same JSX without duplication.

  // Mobile stepper: "1 Campaign Info → 2 Character" with current step gold
  const mobileStepperDisplay = isMobile ? (
    <div
      className="cs-mobile-stepper"
      data-testid="mobile-stepper"
      aria-label="Setup progress"
    >
      <span
        className={`cs-mobile-stepper__step ${step === 1 ? 'cs-mobile-stepper__step--active' : 'cs-mobile-stepper__step--done'}`}
        data-testid="stepper-step-1"
      >
        1 Campaign Info
      </span>
      <span className="cs-mobile-stepper__arrow" aria-hidden="true">→</span>
      <span
        className={`cs-mobile-stepper__step ${step >= 3 ? 'cs-mobile-stepper__step--active' : ''}`}
        data-testid="stepper-step-2"
      >
        2 Character
      </span>
    </div>
  ) : null;

  // Contextual footer CTA label: step 1 → "Next: Character", step 2 → "Start Campaign"
  const footerCtaLabel = (() => {
    if (loading) return 'Creating...';
    if (isMobile) {
      return step === 1 ? 'Next: Character' : 'Start Campaign';
    }
    return 'Start';
  })();

  const innerContent = (
    <div className="campaign-setup-container" data-testid="campaign-setup-modal">
      {/* Mobile stepper — only rendered on mobile */}
      {mobileStepperDisplay}

      {/* Desktop stepper — hidden on mobile via CSS */}
      <div className={`campaign-setup-progress ${isMobile ? 'cs-desktop-only-stepper' : ''}`}>
        <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">Campaign Info</span>
        </div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">Create Characters</span>
        </div>
      </div>

      <div className="campaign-setup-content">
        {step === 1 && renderCampaignInfo()}
        {step >= 3 && renderCharacterCreation()}
      </div>

      {step === 1 && (
        <div className="campaign-setup-footer">
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => handleNextToCharacterSetup()}
            disabled={!campaignInfo.title.trim() || loading}
            variant="primary"
            loading={loading}
            data-testid="campaign-create-submit"
          >
            {footerCtaLabel}
          </Button>
        </div>
      )}
    </div>
  );

  // ─── Mobile: full-screen container (position:fixed; inset:0) ────────────
  if (isMobile) {
    if (!isOpen) return null;
    return (
      <div
        className="cs-mobile-fullscreen"
        data-testid="campaign-setup-fullscreen"
        role="dialog"
        aria-modal="true"
        aria-label="Campaign Setup"
      >
        {/* Close button replaces backdrop tap on mobile */}
        <button
          type="button"
          className="cs-mobile-fullscreen__close"
          onClick={onCancel}
          aria-label="Close campaign setup"
        >
          ✕
        </button>
        {innerContent}
      </div>
    );
  }

  // ─── Desktop: standard Modal ─────────────────────────────────────────────
  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title="Campaign Setup"
      width="max-w-6xl"
      className="h-[85vh]"
      preventBackdropClose
    >
      {innerContent}
    </Modal>
  );
};

export default CampaignSetup;
